import { PolicyLicenseWarning } from './PolicyLicenseWarning';
import { Button, Card, CardContent, Typography } from '@eye-able/ui';
import { IconArchiveOff, IconArrowLeft, IconTrash } from '@tabler/icons-react';
import { Link as RouterLink } from 'react-router-dom';
import { useState } from 'react';
import ConfirmDialog from '@components/ConfirmDialog';
import LoadingSpinner from '@components/LoadingSpinner';
import {
  useFetchPolicies,
  type PolicyResponse,
} from '@http/queries/useFetchPolicies';
import { useDeletePolicy } from '@http/queries/useDeletePolicy';
import { useUnarchivePolicy } from '@http/queries/useUnarchivePolicy';
import { PolicyInventoryCard } from './PolicyInventoryCard';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { useGovernanceAccess } from '@/hooks/useGovernanceAccess';
import { usePolicyEditAccess } from './usePolicyEditAccess';
import { ReadOnlyBanner } from '@/components/access/ReadOnlyBanner';
import { GovernanceAccessBoundary } from '../GovernanceAccessBoundary';
import { usePolicyDomainOptions } from './usePolicyDomainOptions';
import { T } from '@/i18n/T';

function ArchivedPoliciesContent() {
  const { t } = useInlineTranslation();
  const { canEdit } = useGovernanceAccess();
  const { canEditPolicy } = usePolicyEditAccess();
  const { data: policies, isLoading, isError } = useFetchPolicies();
  const deletePolicy = useDeletePolicy();
  const unarchivePolicy = useUnarchivePolicy();
  const [policyToDelete, setPolicyToDelete] = useState<PolicyResponse | null>(
    null,
  );
  const [policyToUnarchive, setPolicyToUnarchive] =
    useState<PolicyResponse | null>(null);

  const handleConfirmDelete = () => {
    if (!canEdit || !policyToDelete || !canEditPolicy(policyToDelete)) return;
    deletePolicy.mutate(policyToDelete.id, {
      onSuccess: () => setPolicyToDelete(null),
    });
  };

  const handleConfirmUnarchive = () => {
    if (!canEdit || !policyToUnarchive || !canEditPolicy(policyToUnarchive)) {
      return;
    }
    unarchivePolicy.mutate(policyToUnarchive, {
      onSuccess: () => setPolicyToUnarchive(null),
    });
  };

  const archivedPolicies = (policies ?? []).filter(
    (policy) => policy.status === 'archived',
  );
  const domainOptions = usePolicyDomainOptions(archivedPolicies);

  return (
    <div className='eye:flex eye:flex-col eye:gap-m'>
      <section className='eye:flex eye:flex-col eye:gap-s'>
        <Button asChild variant='ghost' size='sm' className='eye:self-start'>
          <RouterLink to='/policies'>
            <IconArrowLeft aria-hidden />
            {t('backToPolicies')}
          </RouterLink>
        </Button>

        <div className='eye:max-w-[920px]'>
          <Typography tag='h1' variant='headline-xl'>
            <T k='archivedPolicies' />
          </Typography>
          <Typography className='eye:mt-5xs eye:text-grey-120'>
            <T k='archivedPoliciesDescription' />
          </Typography>
        </div>
      </section>

      <ReadOnlyBanner show={!canEdit} />

      {!isLoading && !isError ? (
        <PolicyLicenseWarning policies={archivedPolicies} />
      ) : null}

      <section className='eye:flex eye:flex-col eye:gap-s'>
        {isLoading ? (
          <LoadingSpinner />
        ) : isError ? (
          <Card>
            <CardContent className='eye:p-s'>
              <Typography className='eye:text-grey-120'>
                <T k='couldNotLoadPolicies' />
              </Typography>
            </CardContent>
          </Card>
        ) : archivedPolicies.length === 0 ? (
          <Card>
            <CardContent className='eye:flex eye:flex-col eye:items-start eye:gap-xs eye:p-s'>
              <Typography tag='h2' variant='headline-s'>
                <T k='noArchivedPolicies' />
              </Typography>
              <Typography className='eye:text-grey-120'>
                <T k='noArchivedPoliciesDescription' />
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <div className='eye:grid eye:gap-s eye:md:grid-cols-2'>
            {archivedPolicies.map((policy) => (
              <PolicyInventoryCard
                key={policy.id}
                policy={policy}
                onDelete={setPolicyToDelete}
                onUnarchive={setPolicyToUnarchive}
                canEdit={canEditPolicy(policy)}
                domainOptions={domainOptions}
              />
            ))}
          </div>
        )}
      </section>

      {canEdit ? (
        <ConfirmDialog
          open={policyToDelete !== null}
          onOpenChange={(open) => {
            if (!open) setPolicyToDelete(null);
          }}
          title={t('deletePolicy')}
          description={
            policyToDelete
              ? t('deletePolicyConfirmation', {
                  policyName: policyToDelete.name,
                })
              : undefined
          }
          confirmText={t('delete')}
          confirmIcon={<IconTrash aria-hidden />}
          loading={deletePolicy.isPending}
          onConfirm={handleConfirmDelete}
        />
      ) : null}

      {canEdit ? (
        <ConfirmDialog
          open={policyToUnarchive !== null}
          onOpenChange={(open) => {
            if (!open) setPolicyToUnarchive(null);
          }}
          title={t('unarchivePolicy')}
          description={
            policyToUnarchive
              ? t('unarchivePolicyConfirmation', {
                  policyName: policyToUnarchive.name,
                })
              : undefined
          }
          confirmText={t('unarchive')}
          confirmIcon={<IconArchiveOff aria-hidden />}
          loading={unarchivePolicy.isPending}
          onConfirm={handleConfirmUnarchive}
        />
      ) : null}
    </div>
  );
}

function ArchivedPolicies() {
  return (
    <GovernanceAccessBoundary>
      <ArchivedPoliciesContent />
    </GovernanceAccessBoundary>
  );
}

export { ArchivedPolicies as Component };
export default ArchivedPolicies;
